import React, { useState } from 'react';
import { Agent } from '../../../types';
import { useAdminTranslation } from '../../../modules/i18n';
import { useTaskManager } from '../../../contexts/TaskManagerContext';
import { ArrowPathIcon, EyeIcon, EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getAgentStatusDisplay } from '../../../utils/agentStatusUtils';
import { getAgentWelcomeMessage } from '../../../utils/agentUtils';

interface MobileAgentCardProps {
  agent: Agent;
  onEdit: () => void;
  onDelete: () => void;
  onViewDetail: () => void;
  onViewProgress?: (jobId: string) => void;
  onToggleHidden?: () => void;
}

const MobileAgentCard: React.FC<MobileAgentCardProps> = ({ agent, onEdit, onDelete, onViewDetail, onViewProgress, onToggleHidden }) => {
  const { t } = useAdminTranslation();
  const { getTasksByNamespace } = useTaskManager();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // Check for active KG import
  const namespace = agent.knowledgeGraph?.namespace;
  const tasks = namespace ? getTasksByNamespace(namespace) : [];
  const activeImport = tasks.find((task) => task.taskType === 'kg_import' && task.status === 'RUNNING');

  const statusDisplay = getAgentStatusDisplay(agent.status, t);
  const isHidden = agent.hidden ?? false;

  const toggleMenu = (event: React.MouseEvent) => {
    if (!showMenu) {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right
      });
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-border-dark dark:bg-dark-background-secondary ${isHidden ? 'opacity-50' : ''}`}>
      <div className='flex items-center justify-between'>
        <div className='flex min-w-0 flex-1 items-center space-x-3'>
          <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'>
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
          </div>
          <div className='min-w-0 flex-1'>
            <h3 className='truncate text-sm font-medium text-gray-900 dark:text-dark-text'>{agent.name}</h3>
            <p className='mt-0.5 truncate text-xs text-gray-500 dark:text-dark-text-secondary'>{getAgentWelcomeMessage(agent)}</p>

            {/* KG Import Progress (if active) */}
            {activeImport && (
              <div className='mt-2 flex items-center gap-2'>
                <ArrowPathIcon className='h-3.5 w-3.5 animate-spin text-blue-500' />
                <div className='h-1.5 flex-1 max-w-[120px] overflow-hidden rounded-full bg-gray-200 dark:bg-dark-background-tertiary'>
                  <div
                    className='h-full rounded-full bg-blue-500 transition-all duration-300'
                    style={{ width: `${activeImport.progressPercentage || 0}%` }}
                  />
                </div>
                <span className='text-xs font-medium text-gray-600 dark:text-dark-text-secondary'>
                  {activeImport.progressPercentage || 0}%
                </span>
                {onViewProgress && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewProgress(activeImport.taskId);
                    }}
                    className='rounded p-0.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-sky-400/50'
                  >
                    <EyeIcon className='h-3.5 w-3.5' />
                  </button>
                )}
              </div>
            )}

            <div className='mt-1 flex items-center space-x-2'>
              {agent.agentType === 'DIRECT_QA' ? (
                <span className='inline-flex rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300'>
                  {t('content.agents.agentType.directQa')}
                </span>
              ) : (
                <span className='inline-flex rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                  {t('content.agents.agentType.kgRag')}
                </span>
              )}
              <span className='inline-flex rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                {agent.language?.name || t('content.agents.status.unknown')}
              </span>
            </div>
          </div>
          <div className='flex-shrink-0'>
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusDisplay.className}`}>{statusDisplay.text}</span>
          </div>
        </div>
        <div className='relative'>
          <button
            onClick={toggleMenu}
            className='rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
            title={t('content.agents.mobile.optionsButton')}
          >
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z'
              />
            </svg>
          </button>

          {showMenu && (
            <>
              <div className='fixed inset-0 z-40' onClick={() => setShowMenu(false)} />
              <div
                className='fixed z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-border-dark dark:bg-dark-background-secondary'
                style={{
                  top: `${menuPosition.top}px`,
                  right: `${menuPosition.right}px`
                }}
              >
                <div className='py-1'>
                  <button
                    onClick={() => {
                      onViewDetail();
                      setShowMenu(false);
                    }}
                    className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                  >
                    <InformationCircleIcon className='h-4 w-4' />
                    <span>{t('content.agents.actions.view')}</span>
                  </button>
                  {onToggleHidden && (
                    <button
                      onClick={() => {
                        onToggleHidden();
                        setShowMenu(false);
                      }}
                      className={`flex w-full items-center space-x-2 px-4 py-2 text-left text-sm ${
                        isHidden
                          ? 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                      }`}
                    >
                      {isHidden ? <EyeIcon className='h-4 w-4' /> : <EyeSlashIcon className='h-4 w-4' />}
                      <span>{isHidden ? t('content.agents.actions.show') : t('content.agents.actions.hide')}</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onEdit();
                      setShowMenu(false);
                    }}
                    className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                  >
                    <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                    <span>{t('content.agents.actions.edit')}</span>
                  </button>
                  <button
                    onClick={() => {
                      onDelete();
                      setShowMenu(false);
                    }}
                    className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-rose-400/50'
                  >
                    <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                      />
                    </svg>
                    <span>{t('content.agents.actions.delete')}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAgentCard;
